import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import useGetAgent from '@/hooks/useGetAgent';
import useGetProfile from '@/hooks/useGetProfile';
import { usePageSeo } from '@/hooks/usePageSeo';
import { cn } from '@/lib/utils';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Award,
  CheckCircle,
  FileText,
  Globe,
  HelpCircle,
  Mail,
  Phone,
  Shield,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { FaRegBuilding } from 'react-icons/fa6';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import Faq from './Faq';
import { GetHelpButton } from './GetHelpButton';
import LoadingModal from './LoadingModal';
import PropertyForm from './PropertyForm';
import ShareMenu from './ShareMenu';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import TutorialModal from './ui/TutorialModal';

const homepageFaqs = [
  {
    question: 'Why is my tax assessment higher than market value?',
    answer:
      "County appraisal districts often use mass appraisal techniques that don't account for specific property issues or recent market conditions. They may use outdated data or comparable properties that aren't truly similar to yours.",
  },
  {
    question: 'What happens after I appeal?',
    answer:
      "After filing an appeal, you'll typically receive a hearing date with your county's appraisal review board. You'll present evidence supporting your claim of a lower value. If successful, your assessed value may be reduced, resulting in lower property taxes.",
  },
  {
    question: 'Is there a deadline to protest property taxes?',
    answer:
      "Yes, most counties have strict deadlines for filing property tax appeals, typically 30 days after receiving your notice of appraised value. Missing this deadline usually means you'll have to wait until next year.",
  },
  {
    question: 'What evidence do I need to gather for my appeal?',
    answer:
      "Effective evidence includes recent comparable sales in your area, photos of property defects that may lower value, independent appraisals, repair estimates, and any documents showing factors that negatively impact your property's value.",
  },
  {
    question: 'Can I appeal my taxes every year?',
    answer:
      "Yes, you have the right to appeal your property tax assessment annually, regardless of previous appeal outcomes. Market conditions change, and your property's fair market value may fluctuate year to year.",
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [query] = useSearchParams();

  const loanOfficer = query.get('loan-officer');
  const mortgage = query.get('mortgage');
  const userType = query.get('user_type');

  const agentIdentifier = useMemo(() => {
    const agent = query.get('agent');
    if (agent) window.localStorage.setItem('agent', agent.replace(' ', '+'));
    const localAgent = window.localStorage.getItem('agent');
    return localAgent;
  }, [query]);

  const { data: profile } = useGetProfile();
  const { isLoading, handleRedirectCallback, loginWithRedirect } = useAuth0();
  const location = useLocation();
  const { data: agent } = useGetAgent(profile?.agent ? null : agentIdentifier);

  usePageSeo({
    title: 'PropertyTaxHero | Official Free Property Tax Appeal Help',
    description:
      'PropertyTaxHero is the official free tool that helps homeowners check for overassessment, understand deadlines, and prepare stronger property tax appeals to potentially lower their property tax bill.',
    path: '/',
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: homepageFaqs.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      },
    ],
  });
  const sponsoredLoanOfficer = useMemo(
    () =>
      agent?.loanOfficerId ?? {
        firstName: 'Rodden',
        lastName: 'Batson',
        phoneNumber: '720-404-9255',
        website: 'www.YourWayLoan.com',
        emailAddress: 'Rodden@YourWayLoan.com',
        photoUrl: '/rodden-batson.webp',
        mortgageId: {
          name: 'Encompass Lending Group, LLC',
          mlsLicense: '292897',
          phoneNumber: '303-554-6886',
          website: 'https://encompasslending.com/',
          imageUrl: '/encompass.webp',
        },
      },
    [agent?.loanOfficerId],
  );

  useEffect(() => {
    if (loanOfficer) {
      window.localStorage.setItem('loan_officer_id', loanOfficer);
      window.localStorage.setItem('user_type', 'AGENT');
    }

    if (mortgage) {
      window.localStorage.setItem('mortgage_id', mortgage);
      window.localStorage.setItem('user_type', 'LOAN_OFFICER');
    }

    if (loanOfficer || mortgage) {
      loginWithRedirect({
        authorizationParams: {
          screen_hint: 'signup',
        },
        appState: {
          returnTo: '/dashboard?tab=profile',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanOfficer, mortgage]);

  useEffect(() => {
    if (loanOfficer)
      window.localStorage.setItem('loan_officer_id', loanOfficer);
  }, [loanOfficer]);

  useEffect(() => {
    if (mortgage) window.localStorage.setItem('mortgage_id', mortgage);
  }, [mortgage]);

  useEffect(() => {
    if (!isLoading && userType) {
      window.localStorage.setItem('user_type', userType ?? 'USER');

      loginWithRedirect({
        authorizationParams: {
          screen_hint: 'signup',
        },
        appState: {
          returnTo: '/dashboard?tab=reports',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, userType]);

  useEffect(() => {
    if (!isLoading && window.location.search.includes('code=')) {
      (async () => {
        const callback = await handleRedirectCallback();
        const returnTo = callback?.appState?.returnTo;

        if (returnTo && returnTo.startsWith('/')) {
          navigate(returnTo, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Add effect to scroll to property form if hash is present
  useEffect(() => {
    if (location.hash === '#property-form') {
      const element = document.getElementById('property-form-section');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  if (isLoading)
    return (
      <div>
        <LoadingModal show />
      </div>
    );

  return (
    <div className="container mx-auto px-4 py-8 relative">
      <div className="block bg-gradient-to-r from-tax-lightGray/50 to-white border border-tax-borderGray rounded-lg shadow-sm mb-4 p-4">
        <div
          className={cn(
            'grid gap-4 grid-cols-1',
            agent ? 'md:grid-cols-3' : 'md:grid-cols-2',
          )}
        >
          {/* Real Estate Agent - Courtesy Of */}
          {agent && (
            <HoverCard openDelay={200}>
              <HoverCardTrigger asChild>
                <div className="flex items-center gap-3 bg-white p-3 rounded-md border border-tax-borderGray hover:shadow-md hover:border-primary/50 transition-all cursor-pointer">
                  <Avatar className="h-12 w-12 flex-shrink-0 border border-primary">
                    <AvatarImage
                      src={agent?.photoUrl}
                      alt={`${agent?.firstName} ${agent?.lastName}`}
                    />
                    <AvatarFallback className="bg-primary/10 text-primary text-sm">
                      {`${agent.firstName[0]}${agent.lastName[0]}`}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-tax-deepBlue mb-0.5">
                      COURTESY OF
                    </p>
                    <h3 className="font-semibold text-sm text-foreground truncate">
                      {`${agent.firstName} ${agent.lastName}`}
                    </h3>
                    <p className="text-xs text-muted-foreground truncate">
                      {agent?.companyName}
                    </p>
                    {agent?.phoneNumber && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <Phone className="h-2.5 w-2.5" />
                        <span className="truncate">{agent?.phoneNumber}</span>
                      </div>
                    )}
                  </div>
                </div>
              </HoverCardTrigger>
              <HoverCardContent className="w-80 p-4" side="bottom">
                <div className="flex gap-4">
                  <Avatar className="h-16 w-16 border-2 border-primary">
                    <AvatarImage
                      src={agent?.photoUrl}
                      alt={`${agent?.firstName} ${agent?.lastName}`}
                    />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {`${agent.firstName[0]}${agent.lastName[0]}`}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-2">
                    <div>
                      <h4 className="font-semibold text-base">
                        {`${agent.firstName} ${agent.lastName}`}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {agent?.companyName || 'PropertyTaxHero'}
                      </p>
                    </div>
                    <div className="space-y-1.5 text-sm">
                      {agent?.phoneNumber && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Phone className="h-3.5 w-3.5" />
                          <a
                            href={`tel:${agent?.phoneNumber}`}
                            className="hover:text-primary transition-colors"
                          >
                            {agent?.phoneNumber}
                          </a>
                        </div>
                      )}
                      {agent?.emailAddress && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Mail className="h-3.5 w-3.5" />
                          <a
                            href={`mailto:${agent?.emailAddress}`}
                            className="hover:text-primary transition-colors truncate max-w-[180px]"
                          >
                            {agent?.emailAddress}
                          </a>
                        </div>
                      )}
                      {agent?.website && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Globe className="h-3.5 w-3.5" />
                          <a
                            href={
                              agent.website.startsWith('http')
                                ? agent.website
                                : `https://${agent.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-primary transition-colors"
                          >
                            {agent?.website}
                          </a>
                        </div>
                      )}
                      {agent?.licenseNumber && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Award className="h-3.5 w-3.5" />
                          <span>License #{agent?.licenseNumber}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>
          )}

          {/* Mortgage Company - Sponsored By */}
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <div className="flex items-center gap-3 bg-white p-3 rounded-md border border-tax-borderGray hover:shadow-md hover:border-primary/50 transition-all cursor-pointer">
                <div className="h-12 w-12 flex-shrink-0 flex items-center justify-center">
                  <Avatar className="h-12 w-12 flex-shrink-0 border border-primary">
                    <AvatarImage src={sponsoredLoanOfficer?.photoUrl} />
                    <AvatarFallback className="bg-primary/10 text-primary text-sm">
                      {`${sponsoredLoanOfficer?.firstName?.[0]}${sponsoredLoanOfficer?.lastName?.[0] ?? ''}`}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-tax-deepBlue mb-0.5">
                    SPONSORED BY
                  </p>
                  <h3 className="font-semibold text-sm text-foreground truncate">
                    {`${sponsoredLoanOfficer.firstName} ${sponsoredLoanOfficer.lastName}`}
                  </h3>
                  {sponsoredLoanOfficer.mortgageId?.name && (
                    <p className="text-xs text-muted-foreground truncate">
                      {sponsoredLoanOfficer.mortgageId?.name}
                    </p>
                  )}
                  {sponsoredLoanOfficer.phoneNumber && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <Phone className="h-2.5 w-2.5" />
                      <span className="truncate">
                        {sponsoredLoanOfficer.phoneNumber}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </HoverCardTrigger>
            <HoverCardContent className="w-80 p-4" side="bottom">
              <div className="flex gap-4">
                <div className="h-16 w-16 flex-shrink-0 flex items-center justify-center border-2 border-primary rounded-md p-2">
                  {sponsoredLoanOfficer.mortgageId?.imageUrl ? (
                    <img
                      src={sponsoredLoanOfficer.mortgageId?.imageUrl}
                      alt={
                        sponsoredLoanOfficer.mortgageId?.name ||
                        'Mortgage Company'
                      }
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <FaRegBuilding className="h-6 w-6 text-tax-actionRed" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <div>
                    <h4 className="font-semibold text-base">
                      {`${sponsoredLoanOfficer.firstName} ${sponsoredLoanOfficer.lastName}`}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {sponsoredLoanOfficer?.mortgageId.name}
                    </p>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    {sponsoredLoanOfficer?.phoneNumber && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-3.5 w-3.5" />
                        <a
                          href={`tel:${sponsoredLoanOfficer?.phoneNumber}`}
                          className="hover:text-primary transition-colors"
                        >
                          {sponsoredLoanOfficer?.phoneNumber}
                        </a>
                      </div>
                    )}
                    {sponsoredLoanOfficer?.emailAddress && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-3.5 w-3.5" />
                        <a
                          href={`mailto:${sponsoredLoanOfficer?.emailAddress}`}
                          className="hover:text-primary transition-colors truncate max-w-[180px]"
                        >
                          {sponsoredLoanOfficer?.emailAddress}
                        </a>
                      </div>
                    )}
                    {sponsoredLoanOfficer?.website && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Globe className="h-3.5 w-3.5" />
                        <a
                          href={
                            sponsoredLoanOfficer.website.startsWith('http')
                              ? sponsoredLoanOfficer?.website
                              : `https://${sponsoredLoanOfficer.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-primary transition-colors"
                        >
                          {sponsoredLoanOfficer?.website}
                        </a>
                      </div>
                    )}
                    {sponsoredLoanOfficer?.mortgageId?.mlsLicense && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Award className="h-3.5 w-3.5" />
                        <span>
                          NMLS #{sponsoredLoanOfficer?.mortgageId?.mlsLicense}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>

          {/* Company - Brought to you by */}
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <div className="flex items-center gap-3 bg-white p-3 rounded-md border border-tax-borderGray hover:shadow-md hover:border-primary/50 transition-all cursor-pointer">
                <div className="h-12 w-12 flex-shrink-0 flex items-center justify-center">
                  <img
                    src="/mortgage-news-logo-icon.png"
                    alt="MortgageNews.org"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-tax-deepBlue mb-0.5">
                    BROUGHT TO YOU BY
                  </p>
                  <h3 className="font-semibold text-sm text-foreground">
                    MortgageNews.org
                  </h3>
                  <a
                    href="https://www.mortgagenews.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 mt-1"
                  >
                    <Globe className="h-2.5 w-2.5" />
                    <span className="truncate">mortgagenews.org</span>
                  </a>
                </div>
              </div>
            </HoverCardTrigger>
            <HoverCardContent className="w-80 p-4" side="bottom">
              <div className="flex gap-4">
                <div className="h-16 w-16 flex-shrink-0 flex items-center justify-center border-2 border-primary rounded-md p-2">
                  <img
                    src="/mortgage-news-logo-icon.png"
                    alt="MortgageNews.org"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <div>
                    <h4 className="font-semibold text-base">
                      MortgageNews.org
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Your Source for Mortgage Industry News
                    </p>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <p className="text-muted-foreground text-xs leading-relaxed">
                      The latest news, trends, and insights in the mortgage and
                      real estate industry.
                    </p>
                    <div className="flex items-center gap-2 text-muted-foreground pt-1">
                      <Globe className="h-3.5 w-3.5" />
                      <a
                        href="https://www.mortgagenews.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors font-medium"
                      >
                        Visit mortgagenews.org
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        </div>
      </div>

      {/* Hero */}
      <section className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-bold tracking-tight mb-6 text-tax-deepBlue text-3xl md:text-5xl">
            Fight Unfair Property Taxes
          </h1>
          <p className="text-lg md:text-xl text-tax-neutralGray mb-4 max-w-3xl mx-auto">
            PropertyTaxHero helps homeowners protest unfair property taxes with
            real-time MLS data, an interactive value calculator, AI-driven
            guidance, and ready-to-submit appeals.
          </p>

          {/* Trust bullets */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {[
              { icon: CheckCircle, text: 'Free to use' },
              { icon: CheckCircle, text: 'Real-time MLS data' },
              { icon: CheckCircle, text: 'Ready-to-submit reports' },
              { icon: Shield, text: 'We never sell your data' },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-2 bg-white border border-tax-borderGray rounded-full px-4 py-2 shadow-sm"
              >
                <item.icon className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-tax-deepBlue">
                  {item.text}
                </span>
              </div>
            ))}
          </div>

          {/* How it works */}
          <div className="py-8 bg-tax-lightGray rounded-lg mb-8">
            <div className="max-w-4xl mx-auto px-4">
              <h2 className="text-3xl font-semibold text-center mb-12 text-tax-deepBlue">
                How PropertyTaxHero Helps Lower Property Tax Bills
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  {
                    n: '1',
                    title: 'Find Your Property',
                    desc: "Enter your address below and we'll retrieve all the pertinent data necessary to start an appeal.",
                  },
                  {
                    n: '2',
                    title: 'Build Your Case',
                    desc: "We'll help you find comparable sales data necessary to argue your case for a lower value.",
                  },
                  {
                    n: '3',
                    title: 'File Your Appeal',
                    desc: "We'll give you a complete letter, report and instructions on submitting it to your local assessor.",
                  },
                ].map((step) => (
                  <div
                    key={step.n}
                    className="bg-white p-6 rounded-lg shadow-sm border border-tax-borderGray"
                  >
                    <div className="h-12 w-12 bg-accent rounded-full flex items-center justify-center mb-4 mx-auto">
                      <span className="text-accent-foreground font-bold text-xl">
                        {step.n}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-center text-tax-deepBlue">
                      {step.title}
                    </h3>
                    <p className="text-tax-neutralGray text-center">
                      {step.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Property form */}
          <div id="property-form-section" className="mb-12 scroll-mt-24">
            <div className="space-y-4">
              <div className="flex flex-col md:flex-row items-center justify-between mb-2 space-y-2 md:space-y-0">
                <div className="w-[108px] hidden md:block" />
                <div className="flex items-center justify-center gap-3">
                  <div className="h-8 w-8 bg-accent rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-accent-foreground font-bold text-md">
                      1
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-tax-deepBlue">
                    ENTER YOUR PROPERTY ADDRESS
                  </h3>
                </div>
                <GetHelpButton />
              </div>
              <PropertyForm />
            </div>
          </div>
        </div>
      </section>

      {/* Why Homeowners Use PropertyTaxHero */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-semibold text-center mb-4 text-tax-deepBlue">
            Why Homeowners Use PropertyTaxHero
          </h2>
          <p className="text-lg text-center text-tax-neutralGray mb-12 max-w-2xl mx-auto">
            Thousands of homeowners trust PropertyTaxHero to check their
            assessments and prepare appeals — completely free of charge.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="border border-tax-borderGray rounded-lg p-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 bg-tax-deepBlue/10 rounded-full flex items-center justify-center mr-4">
                  <FileText className="h-5 w-5 text-tax-deepBlue" />
                </div>
                <h3 className="text-xl font-semibold text-tax-deepBlue">
                  County Errors Are Common
                </h3>
              </div>
              <p className="text-tax-neutralGray">
                Mass appraisal techniques often miss important details about
                your specific property, leading to overvaluation and higher tax
                bills.
              </p>
            </div>
            <div className="border border-tax-borderGray rounded-lg p-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 bg-tax-deepBlue/10 rounded-full flex items-center justify-center mr-4">
                  <HelpCircle className="h-5 w-5 text-tax-deepBlue" />
                </div>
                <h3 className="text-xl font-semibold text-tax-deepBlue">
                  Save Money Every Year
                </h3>
              </div>
              <p className="text-tax-neutralGray">
                A successful appeal can lower your property tax bill for the
                current year and potentially set a precedent for future
                assessments.
              </p>
            </div>
            <div className="border border-tax-borderGray rounded-lg p-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 bg-tax-deepBlue/10 rounded-full flex items-center justify-center mr-4">
                  <Shield className="h-5 w-5 text-tax-deepBlue" />
                </div>
                <h3 className="text-xl font-semibold text-tax-deepBlue">
                  100% Free, No Data Selling
                </h3>
              </div>
              <p className="text-tax-neutralGray">
                PropertyTaxHero is completely free. We never charge homeowners
                and we never sell your personal information.
              </p>
            </div>
            <div className="border border-tax-borderGray rounded-lg p-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 bg-tax-deepBlue/10 rounded-full flex items-center justify-center mr-4">
                  <CheckCircle className="h-5 w-5 text-tax-deepBlue" />
                </div>
                <h3 className="text-xl font-semibold text-tax-deepBlue">
                  Covers All 50 States
                </h3>
              </div>
              <p className="text-tax-neutralGray">
                From{' '}
                <Link
                  to="/texas"
                  className="text-tax-brightBlue hover:underline"
                >
                  Texas
                </Link>{' '}
                to{' '}
                <Link
                  to="/california"
                  className="text-tax-brightBlue hover:underline"
                >
                  California
                </Link>
                ,{' '}
                <Link
                  to="/florida"
                  className="text-tax-brightBlue hover:underline"
                >
                  Florida
                </Link>{' '}
                to{' '}
                <Link
                  to="/new-york"
                  className="text-tax-brightBlue hover:underline"
                >
                  New York
                </Link>{' '}
                — we provide appeal guides, deadlines, and comparable sales data
                for every state.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold text-center mb-6 text-tax-deepBlue">
            Property Tax Appeal Resources
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
            <Link
              to="/how-to-appeal-property-tax"
              className="p-4 border border-tax-borderGray rounded-lg hover:shadow-md transition-shadow text-tax-deepBlue font-medium hover:text-tax-brightBlue"
            >
              How to Appeal
            </Link>
            <Link
              to="/lower-property-tax-bill"
              className="p-4 border border-tax-borderGray rounded-lg hover:shadow-md transition-shadow text-tax-deepBlue font-medium hover:text-tax-brightBlue"
            >
              Lower Your Tax Bill
            </Link>
            <Link
              to="/property-tax-protest"
              className="p-4 border border-tax-borderGray rounded-lg hover:shadow-md transition-shadow text-tax-deepBlue font-medium hover:text-tax-brightBlue"
            >
              Protest Guide
            </Link>
            <Link
              to="/property-tax-comparable-sales-guide"
              className="p-4 border border-tax-borderGray rounded-lg hover:shadow-md transition-shadow text-tax-deepBlue font-medium hover:text-tax-brightBlue"
            >
              Comparable Sales
            </Link>
            <Link
              to="/property-tax-appeal-deadlines"
              className="p-4 border border-tax-borderGray rounded-lg hover:shadow-md transition-shadow text-tax-deepBlue font-medium hover:text-tax-brightBlue"
            >
              Appeal Deadlines
            </Link>
            <Link
              to="/all-states"
              className="p-4 border border-tax-borderGray rounded-lg hover:shadow-md transition-shadow text-tax-deepBlue font-medium hover:text-tax-brightBlue"
            >
              All 50 State Guides
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-16 bg-tax-lightGray rounded-lg">
        <div className="max-w-4xl mx-auto px-4">
          <Faq />
          <div className="mt-10 text-center">
            <Button
              asChild
              variant="outline"
              className="max-w-xs min-h-[3rem] px-6 bg-primary border-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground rounded-lg"
            >
              <Link to="/faq">View All FAQs</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
export default HomePage;
